import { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";
import i18n from "@/i18n";
import { Sentry } from "@/lib/sentry";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Forward render errors to Sentry. The Sentry init in main.tsx is a
    // no-op when VITE_SENTRY_DSN is not set, so this is also a no-op
    // in dev / unconfigured environments.
    try {
      Sentry.withScope((scope) => {
        scope.setExtra("componentStack", info.componentStack);
        Sentry.captureException(error);
      });
    } catch {
      // never let telemetry break the error UI
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const t = (key: string) => i18n.t(key, { ns: "common" });
      return (
        <div className="min-h-screen bg-[#07070a] flex items-center justify-center p-6">
          <div className="max-w-md w-full text-center">
            <div className="w-20 h-20 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-10 h-10 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-3">{t("errorTitle")}</h1>
            <p className="text-white/50 text-sm leading-relaxed mb-8">
              {t("errorDescription")}
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={this.handleRetry}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-vl-orange to-vl-deep text-white rounded-xl font-semibold shadow-lg shadow-vl-deep/25 hover:opacity-90 transition-all"
              >
                <RotateCcw className="w-4 h-4" />
                {t("tryAgain")}
              </button>
              <button
                onClick={this.handleReload}
                className="px-6 py-3 rounded-xl font-medium text-white/60 hover:text-white border border-white/10 hover:bg-white/5 transition-all"
              >
                {t("reloadPage")}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
